import { MapPin, Video, UserRound, Clock } from "lucide-react";
import { useScrollReveal } from "../lib/useScrollReveal";

const CARD_BASE =
  "relative rounded-[28px] p-9 md:p-10 flex flex-col h-full transition-all duration-500 ease-out";

const focusAreas = [
  "Weight Management",
  "PCOD",
  "PCOS",
  "Irregular Periods",
  "Strength Building",
  "Flexibility",
  "Inner Balance",
];

export const Classes = ({ onBook, waLink }) => {
  const ref = useScrollReveal();

  // Build action props for CTA buttons. When `waLink` is provided, render
  // anchors that open WhatsApp; otherwise fall back to the booking dialog.
  const actionProps = waLink
    ? {
        as: "a",
        extraProps: { href: waLink, target: "_blank", rel: "noreferrer" },
      }
    : { as: "button", extraProps: { onClick: onBook, type: "button" } };
  const ActionTag = actionProps.as;
  const extra = actionProps.extraProps;

  const Card1And3Style =
    "bg-white/75 backdrop-blur-xl border border-[#1F4A38]/10 shadow-[0_12px_40px_rgba(31,74,56,0.08)] hover:shadow-[0_22px_60px_rgba(31,74,56,0.14)] hover:-translate-y-2";

  const Card2Style =
    "bg-[#1F4A38] text-[#F9F6F0] shadow-[0_18px_50px_rgba(31,74,56,0.28)] hover:shadow-[0_26px_70px_rgba(31,74,56,0.4)] hover:-translate-y-2 border border-[#D4A24C]/30";

  return (
    <section
      id="classes"
      data-testid="classes-section"
      ref={ref}
      className="relative py-24 md:py-36 bg-[#F9F6F0]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-16 md:mb-20">
          <p
            className="reveal text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-5 font-medium"
            data-testid="classes-eyebrow"
          >
            Our Offerings
          </p>
          <h2
            data-testid="classes-heading"
            className="reveal font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#153326] leading-[1.05] font-light"
          >
            Made For <em className="italic text-[#1F4A38]">Your</em> Schedule
          </h2>
          <p className="reveal mt-6 text-[#2C5F4A]/80 text-base md:text-lg font-light">
            Thoughtfully designed formats — choose the container that fits your
            rhythm, goals and space.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7 items-stretch">
          {/* Card 1 */}
          <article
            data-testid="card-group-offline"
            className={`reveal ${CARD_BASE} ${Card1And3Style}`}
          >
            <div className="w-14 h-14 rounded-2xl bg-[#1F4A38]/10 text-[#1F4A38] flex items-center justify-center mb-6">
              <UserRound size={26} strokeWidth={1.5} />
            </div>
            <h3 className="font-serif-display text-3xl text-[#153326] font-medium">
              Group Offline Class
            </h3>
            <p className="mt-3 flex items-center gap-2 text-[#2C5F4A] text-sm">
              <MapPin size={15} className="text-[#D4A24C]" />
              ToutleSpace, Malad, Mumbai
            </p>

            <div className="my-7 h-px bg-[#1F4A38]/10" />

            <p className="text-xs tracking-[0.25em] uppercase text-[#2C5F4A]/70 mb-3">
              Schedule
            </p>
            <p className="text-[#153326] font-medium">Mon – Friday · 20 Classes / Month</p>
            <div className="mt-3 space-y-1.5 text-[#2C5F4A] text-[15px]">
              <p className="flex items-center gap-2">
                <Clock size={14} className="text-[#D4A24C]" /> 6:00 AM – 7:00 AM
              </p>
              <p className="flex items-center gap-2">
                <Clock size={14} className="text-[#D4A24C]" /> 7:15 AM – 8:15 AM
              </p>
            </div>

            <div className="mt-auto pt-8">
              <ActionTag
                data-testid="book-offline-btn"
                {...extra}
                className="block text-center w-full bg-[#1F4A38] hover:bg-[#153326] text-[#F9F6F0] rounded-full px-6 py-3.5 text-sm font-semibold tracking-wide transition-all duration-300 hover:shadow-lg"
              >
                Book a Spot
              </ActionTag>
              <p className="mt-4 text-center text-xs text-[#2C5F4A]/70 tracking-wide">
                Limited Students · Personal Attention
              </p>
            </div>
          </article>

          {/* Card 2 – distinct */}
          <article
            data-testid="card-group-online"
            className={`reveal ${CARD_BASE} ${Card2Style} md:scale-[1.02] md:-my-2`}
          >
            <div className="absolute top-5 right-5 text-[10px] tracking-[0.3em] uppercase text-[#D4A24C]">
              Popular
            </div>
            <div className="w-14 h-14 rounded-2xl bg-[#D4A24C]/15 text-[#D4A24C] flex items-center justify-center mb-6">
              <Video size={26} strokeWidth={1.5} />
            </div>
            <h3 className="font-serif-display text-3xl text-white font-medium">
              Group Online Class
            </h3>
            <p className="mt-3 text-[#EAC98A] text-sm">Live via Zoom / Google Meet</p>
            <p className="text-[#F9F6F0]/80 text-sm italic">Join from anywhere</p>

            <div className="my-7 h-px bg-white/15" />

            <p className="text-xs tracking-[0.25em] uppercase text-[#EAC98A]/90 mb-3">
              Schedule
            </p>
            <p className="text-white font-medium">Mon – Friday · 20 Classes / Month</p>
            <div className="mt-3 space-y-1.5 text-[#F9F6F0]/90 text-[15px]">
              <p className="flex items-center gap-2">
                <Clock size={14} className="text-[#D4A24C]" /> 7:00 PM – 8:00 PM
              </p>
              <p className="flex items-center gap-2">
                <Clock size={14} className="text-[#D4A24C]" /> 8:15 PM – 9:15 PM
              </p>
            </div>

            <div className="mt-auto pt-8">
              <ActionTag
                data-testid="join-online-btn"
                {...extra}
                className="block text-center w-full bg-[#D4A24C] hover:bg-[#C29344] text-[#153326] rounded-full px-6 py-3.5 text-sm font-semibold tracking-wide transition-all duration-300 hover:shadow-lg"
              >
                Join Online
              </ActionTag>
              <p className="mt-4 text-center text-xs text-[#F9F6F0]/70 tracking-wide">
                Limited Students · Personal Attention
              </p>
            </div>
          </article>

          {/* Card 3 */}
          <article
            data-testid="card-private-session"
            className={`reveal ${CARD_BASE} ${Card1And3Style}`}
          >
            <div className="w-14 h-14 rounded-2xl bg-[#1F4A38]/10 text-[#1F4A38] flex items-center justify-center mb-6">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="7" r="3"/><path d="M5 21c0-4 3.5-7 7-7s7 3 7 7"/></svg>
            </div>
            <h3 className="font-serif-display text-3xl text-[#153326] font-medium">
              Private Session
            </h3>
            <p className="mt-3 text-[#2C5F4A] text-sm">One-on-One · Your Schedule</p>

            <div className="my-7 h-px bg-[#1F4A38]/10" />

            <p className="text-xs tracking-[0.25em] uppercase text-[#2C5F4A]/70 mb-3">
              Timing
            </p>
            <p className="text-[#153326] font-medium">Flexible Timing</p>
            <div className="mt-3 space-y-1.5 text-[#2C5F4A] text-[15px]">
              <p><span className="text-[#D4A24C] font-medium">Saturday:</span> Arrange as per availability</p>
              <p><span className="text-[#D4A24C] font-medium">Sunday:</span> Book any 1-hour slot</p>
            </div>

            <div className="mt-auto pt-8">
              <ActionTag
                data-testid="enquire-private-btn"
                {...extra}
                className="block text-center w-full bg-[#1F4A38] hover:bg-[#153326] text-[#F9F6F0] rounded-full px-6 py-3.5 text-sm font-semibold tracking-wide transition-all duration-300 hover:shadow-lg"
              >
                Enquire Now
              </ActionTag>
              <div className="mt-5 flex flex-wrap gap-1.5 justify-center">
                {focusAreas.map((f) => (
                  <span
                    key={f}
                    className="text-[10.5px] tracking-wide px-2.5 py-1 rounded-full bg-[#1F4A38]/6 text-[#2C5F4A] border border-[#1F4A38]/10"
                  >
                    {f}
                  </span>
                ))}
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

export default Classes;
